import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SwUpdate } from "@angular/service-worker";
import { WA_LOCAL_STORAGE, WA_WINDOW } from "@ng-web-apis/common";
import { gt, valid } from "semver";
import { verifyPlayerRelease } from "./verify-player-release";

const UPDATE_CHECK_INTERVAL_MS = 10 * 60_000;
const RELOAD_COOLDOWN_MS = 60 * 60_000;
const STALLED_UPDATE_MS = 20 * 60_000;
const VERIFIED_RELEASE_TTL_MS = 15 * 60_000;
const VERIFIED_RELEASE_REFRESH_MS = 5 * 60_000;
const REGISTRATION_GRACE_MS = 60_000;
const UPDATE_CHECK_TIMEOUT_MS = 15_000;
const PREPARE_RELOAD_TIMEOUT_MS = 2_000;

@Injectable({ providedIn: "root" })
export class SwUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly window = inject(WA_WINDOW);
  private readonly storage = inject(WA_LOCAL_STORAGE);
  private readonly serviceWorker = (() => {
    try {
      return this.window.navigator.serviceWorker;
    } catch {
      return undefined;
    }
  })();
  private readonly destroyRef = inject(DestroyRef);
  private initialized = false;
  private intervalId: number | undefined;
  private checking: Promise<void> | undefined;
  private pendingVersion: string | undefined;
  private remoteVersion: string | undefined;
  private remoteVersionSeenAt = 0;
  private verifiedRepair: { version: string; at: number } | undefined;
  private verifyingRepair = false;
  private retiringWorker: Promise<boolean> | undefined;
  private workerRetired = false;
  private preparationTimeouts = 0;
  private registrationGraceElapsed = false;
  private registrationTimer: number | undefined;
  private checkVersion: (() => void) | undefined;
  private readonly onControllerChange = () => {
    void this.checkForUpdates();
  };
  private playbackBusy = true;
  private preparing = false;
  private reloading = false;
  private beforeReload: ((signal: AbortSignal) => Promise<boolean>) | undefined;
  private readonly onOnline = () => {
    this.poll();
  };

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.intervalId !== undefined) this.window.clearInterval(this.intervalId);
      this.window.removeEventListener("online", this.onOnline);
      if (this.registrationTimer !== undefined) this.window.clearTimeout(this.registrationTimer);
      this.serviceWorker?.removeEventListener("controllerchange", this.onControllerChange);
    });
  }

  // The outbox registers this without making the updater depend on AppStore.
  setReloadPreparation(prepare: (signal: AbortSignal) => Promise<boolean>): void {
    this.beforeReload = prepare;
  }

  setVersionCheck(check: () => void): void {
    this.checkVersion = check;
  }

  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.serviceWorker?.addEventListener("controllerchange", this.onControllerChange);
    this.registrationTimer = this.window.setTimeout(() => {
      this.registrationGraceElapsed = true;
      this.poll();
    }, REGISTRATION_GRACE_MS);
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
        if (event.type === "VERSION_READY") {
          this.pendingVersion = `sw:${event.latestVersion.hash}`;
          void this.reloadIfSafe(() => !this.playbackBusy);
        } else if (event.type === "VERSION_INSTALLATION_FAILED") {
          // Keep the working application and its offline media cache intact.
          console.warn("Unable to install player update; keeping the current version");
        }
      });
      this.swUpdate.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        // Angular cannot repair the running version without a navigation.
        this.pendingVersion = "recovery";
        void this.reloadIfSafe(() => !this.playbackBusy);
      });
    }
    this.window.addEventListener("online", this.onOnline);
    this.intervalId = this.window.setInterval(() => {
      this.poll();
    }, UPDATE_CHECK_INTERVAL_MS);
    // Do not delay language, connection state or startup on the update request.
    this.poll();
  }

  private poll(): void {
    if (this.destroyRef.destroyed || !this.window.navigator.onLine) return;
    this.checkVersion?.();
    void this.checkForUpdates();
  }

  notifyRemoteVersion(remote: string, current: string): void {
    const version = remote.trim();
    if (!valid(version) || !valid(current)) return;
    const nextVersion = gt(version, current) ? version : undefined;
    if (nextVersion !== this.remoteVersion) {
      if (!this.remoteVersion || !nextVersion) this.remoteVersionSeenAt = Date.now();
      this.verifiedRepair = undefined;
    }
    this.remoteVersion = nextVersion;
    if (this.initialized) void this.checkForUpdates();
  }

  setPlaybackBusy(busy: boolean): void {
    this.playbackBusy = busy;
    if (!busy) void this.reloadIfSafe(() => !this.playbackBusy);
  }

  hasPendingUpdate(): boolean {
    return !!this.getPendingUpdate() && !this.reloading;
  }

  reloadAtBoundary(isStillAtBoundary: () => boolean): Promise<boolean> {
    return this.reloadIfSafe(isStillAtBoundary);
  }

  private getPendingUpdate(): string | undefined {
    if (this.pendingVersion) return this.pendingVersion;
    if (
      this.verifiedRepair?.version === this.remoteVersion &&
      this.verifiedRepair &&
      Date.now() - this.verifiedRepair.at >= 0 &&
      Date.now() - this.verifiedRepair.at <= VERIFIED_RELEASE_TTL_MS
    ) {
      return `repair:${this.remoteVersion}`;
    }
    // An enabled worker may never register. Never bypass an existing controller,
    // including one left over from a previous release with SW now disabled.
    if (
      this.hasController() !== false ||
      (this.swUpdate.isEnabled && !this.registrationGraceElapsed)
    )
      return undefined;
    return this.remoteVersion ? `version:${this.remoteVersion}` : undefined;
  }

  private hasController(): boolean | undefined {
    try {
      return !!this.window.navigator.serviceWorker?.controller;
    } catch {
      return undefined;
    }
  }

  private async checkForUpdates(): Promise<void> {
    if (!this.window.navigator.onLine || this.destroyRef.destroyed) return;
    void this.verifyStalledUpdate();
    // Idle recovery must not wait for another (possibly hung) SW operation.
    void this.reloadIfSafe(() => !this.playbackBusy);
    if (this.checking) return this.checking;
    if (!this.swUpdate.isEnabled || this.hasController() !== true) return;
    const check = async () => {
      let timeoutId: number | undefined;
      try {
        await Promise.race([
          this.swUpdate.checkForUpdate(),
          new Promise<never>((_, reject) => {
            timeoutId = this.window.setTimeout(
              () => reject(new Error("Update check timed out")),
              UPDATE_CHECK_TIMEOUT_MS,
            );
          }),
        ]);
      } catch {
        console.warn("Unable to check for player updates; will retry later");
      } finally {
        if (timeoutId !== undefined) this.window.clearTimeout(timeoutId);
      }
      await this.reloadIfSafe(() => !this.playbackBusy);
    };
    this.checking = check();
    try {
      await this.checking;
    } finally {
      this.checking = undefined;
    }
  }

  private async verifyStalledUpdate(): Promise<void> {
    const version = this.remoteVersion;
    if (!version || this.pendingVersion || this.verifyingRepair || this.hasController() !== true)
      return;
    if (Date.now() < this.remoteVersionSeenAt) this.remoteVersionSeenAt = Date.now();
    if (Date.now() - this.remoteVersionSeenAt < STALLED_UPDATE_MS) return;
    if (
      this.verifiedRepair?.version === version &&
      Date.now() - this.verifiedRepair.at < VERIFIED_RELEASE_REFRESH_MS
    )
      return;
    this.verifyingRepair = true;
    const abort = new AbortController();
    const timer = this.window.setTimeout(() => abort.abort(), UPDATE_CHECK_TIMEOUT_MS);
    try {
      const verified = await verifyPlayerRelease(
        this.window.fetch.bind(this.window),
        this.window.location.href,
        version,
        abort.signal,
      );
      if (
        verified &&
        !abort.signal.aborted &&
        !this.destroyRef.destroyed &&
        this.remoteVersion === version
      ) {
        this.verifiedRepair = { version, at: Date.now() };
        void this.reloadIfSafe(() => !this.playbackBusy);
      }
    } catch (error) {
      console.warn("Player deployment is not ready for worker recovery; will retry later", error);
    } finally {
      abort.abort();
      this.window.clearTimeout(timer);
      this.verifyingRepair = false;
    }
  }

  private canReload(): boolean {
    if (
      !this.storage ||
      !this.getPendingUpdate() ||
      !this.beforeReload ||
      !this.window.navigator.onLine ||
      this.destroyRef.destroyed
    )
      return false;
    try {
      const raw = this.storage.getItem("lastReload");
      if (!raw) return true;
      const previous = Number(raw);
      if (!Number.isFinite(previous) || previous < 0 || previous > Date.now()) {
        // Clock corrections/corrupt storage must not disable updates indefinitely.
        this.storage.setItem("lastReload", Date.now().toString());
        return false;
      }
      return Date.now() - previous >= RELOAD_COOLDOWN_MS;
    } catch {
      // Without a durable guard, a failed deployment could cause a reload loop.
      return false;
    }
  }

  private async reloadIfSafe(isSafe: () => boolean): Promise<boolean> {
    if (this.preparing || this.reloading || !isSafe() || !this.canReload()) return false;
    const storage = this.storage;
    if (!storage) return false;
    const pending = this.getPendingUpdate();
    this.preparing = true;
    const abort = new AbortController();
    let timeoutId: number | undefined;
    try {
      const prepared = await Promise.race([
        (async () => {
          if (pending?.startsWith("version:") || pending?.startsWith("repair:")) {
            // navigator.onLine does not prove that the deployment is reachable.
            // Revalidate the version over the network immediately before leaving
            // a working document, and abort slow requests with the outer deadline.
            const response = await this.window.fetch("/assets/VERSION.txt?ngsw-bypass=true", {
              cache: "no-store",
              signal: abort.signal,
            });
            if (
              !response.ok ||
              (await response.text()).trim() !== pending.slice(pending.indexOf(":") + 1)
            )
              return false;
          }
          if (abort.signal.aborted || this.getPendingUpdate() !== pending) return false;
          if (
            !(await this.beforeReload!(abort.signal)) ||
            abort.signal.aborted ||
            !isSafe() ||
            !this.canReload() ||
            this.getPendingUpdate() !== pending
          )
            return false;
          if (pending?.startsWith("repair:") && !this.workerRetired) {
            const controller = this.serviceWorker?.controller;
            const registration = await this.serviceWorker?.getRegistration();
            if (abort.signal.aborted || !isSafe() || this.getPendingUpdate() !== pending)
              return false;
            if (registration) {
              const expectedScript = new URL("/ngsw-worker.js", this.window.location.href).href;
              if (
                !controller ||
                registration.active !== controller ||
                controller.scriptURL !== expectedScript
              )
                return false;
              // unregister cannot be cancelled. Track it beyond the preparation
              // deadline; a late success remains eligible for the next boundary.
              if (!this.retiringWorker) {
                this.retiringWorker = registration.unregister().then(
                  (retired) => {
                    this.workerRetired = retired;
                    this.retiringWorker = undefined;
                    if (retired && !abort.signal.aborted) return true;
                    if (retired) void this.reloadIfSafe(() => !this.playbackBusy);
                    return retired;
                  },
                  () => {
                    this.retiringWorker = undefined;
                    return false;
                  },
                );
              }
              if (!(await this.retiringWorker)) return false;
            }
          }
          return true;
        })(),
        new Promise<boolean>((resolve) => {
          timeoutId = this.window.setTimeout(
            () => {
              this.preparationTimeouts = Math.min(this.preparationTimeouts + 1, 4);
              resolve(false);
            },
            Math.min(PREPARE_RELOAD_TIMEOUT_MS * 2 ** this.preparationTimeouts, 30_000),
          );
        }),
      ]);
      if (!prepared || !isSafe() || !this.canReload() || this.getPendingUpdate() !== pending)
        return false;
      this.preparationTimeouts = 0;
      storage.setItem("lastReload", Date.now().toString());
      this.reloading = true;
      // A navigation selects the downloaded version atomically. Do not activate a
      // new worker beneath JavaScript still running from the previous build.
      this.window.location.reload();
      return true;
    } catch {
      this.reloading = false;
      console.warn("Player update postponed; playback will continue");
      return false;
    } finally {
      abort.abort();
      if (timeoutId !== undefined) this.window.clearTimeout(timeoutId);
      this.preparing = false;
    }
  }
}
